/* eslint-disable max-len */
/**
 * 弹跳动画
 * 路径 + 滤镜复合动画
 * 滤镜部分参考文件 @src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-filters/effects/bounce.ts
 * 入场动画基本思路是参考弹跳的退场动画，从不同阶段的路径正弦函数中取出 11 个点作为关键帧
 * 时间节点上 x 轴的 tmFilter 和 scaleY 都向 y 轴关键阶段（到达最底端）时间点对齐
 */
import { presetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import { AnimType } from '@tencent/slide-sdk-model';
import _isNumber from 'lodash/isNumber';
import { getAnimDuration, getSlideWidthAndHeight, isRightAnimType } from '../utils';
import { IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { PLAY_MODE } from '../constants';

const DEFAULT_ANIM_DURATION = 2000;

type TAnimBrevity = {
    duration: number,
    value: number
};

// 参考退场动画，此处为计算后位移节点
const entrYTavList = [
    {
        delay: 0,
        duration: 664,
        tav: [
            {
                tm: 0,
                val: -0.33,
            },
            {
                tm: 5000,
                val: -0.33,
            },
            {
                tm: 10000,
                val: -0.33,
            },
            {
                tm: 15000,
                val: -0.33,
            },
            {
                tm: 20000,
                val: -0.31,
            },
            {
                tm: 30000,
                val: -0.30,
            },
            {
                tm: 40000,
                val: -0.27,
            },
            {
                tm: 50000,
                val: -0.24,
            },
            {
                tm: 60000,
                val: -0.20,
            },
            {
                tm: 70000,
                val: -0.15,
            },
            {
                tm: 80000,
                val: -0.10,
            },
            {
                tm: 90000,
                val: -0.052,
            },
            {
                tm: 100000,
                val: 0,
            },
        ],
    },
    {
        delay: 664,
        duration: 664,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 10000,
                val: -0.034,
            },
            {
                tm: 20000,
                val: -0.065,
            },
            {
                tm: 30000,
                val: -0.090,
            },
            {
                tm: 40000,
                val: -0.106,
            },
            {
                tm: 50000,
                val: -0.111,
            },
            {
                tm: 60000,
                val: -0.106,
            },
            {
                tm: 70000,
                val: -0.090,
            },
            {
                tm: 80000,
                val: -0.065,
            },
            {
                tm: 90000,
                val: -0.034,
            },
            {
                tm: 100000,
                val: 0,
            },
        ],
    },
    {
        delay: 1328,
        duration: 332,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 10000,
                val: -0.011,
            },
            {
                tm: 20000,
                val: -0.022,
            },
            {
                tm: 30000,
                val: -0.030,
            },
            {
                tm: 40000,
                val: -0.035,
            },
            {
                tm: 50000,
                val: -0.037,
            },
            {
                tm: 60000,
                val: -0.035,
            },
            {
                tm: 70000,
                val: -0.030,
            },
            {
                tm: 80000,
                val: -0.022,
            },
            {
                tm: 90000,
                val: -0.011,
            },
            {
                tm: 100000,
                val: 0,
            },
        ],
    },
    {
        delay: 1660,
        duration: 164,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 10000,
                val: -0.004,
            },
            {
                tm: 20000,
                val: -0.007,
            },
            {
                tm: 30000,
                val: -0.010,
            },
            {
                tm: 40000,
                val: -0.012,
            },
            {
                tm: 50000,
                val: -0.012,
            },
            {
                tm: 60000,
                val: -0.012,
            },
            {
                tm: 70000,
                val: -0.010,
            },
            {
                tm: 80000,
                val: -0.007,
            },
            {
                tm: 90000,
                val: -0.004,
            },
            {
                tm: 100000,
                val: 0,
            },
        ],
    },
    {
        delay: 1820,
        duration: 180,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 100000,
                val: 0,
            },
        ],
    },
];

// NOTE: 此处为精简和计算后的结构，完整结构参考 OXML
const exitYTavList = [
    {
        delay: 0,
        duration: 664,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 5000,
                val: +0.026,
            },
            {
                tm: 10000,
                val: +0.052,
            },
            {
                tm: 15000,
                val: +0.078,
            },
            {
                tm: 20000,
                val: +0.103,
            },
            {
                tm: 30000,
                val: +0.151,
            },
            {
                tm: 40000,
                val: +0.196,
            },
            {
                tm: 50000,
                val: +0.236,
            },
            {
                tm: 60000,
                val: +0.270,
            },
            {
                tm: 70000,
                val: +0.297,
            },
            {
                tm: 80000,
                val: +0.317,
            },
            {
                tm: 90000,
                val: +0.329,
            },
            {
                tm: 100000,
                val: +0.333,
            },
        ],
    },
    {
        delay: 664,
        duration: 664,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 10000,
                val: -0.034,
            },
            {
                tm: 20000,
                val: -0.065,
            },
            {
                tm: 30000,
                val: -0.090,
            },
            {
                tm: 40000,
                val: -0.106,
            },
            {
                tm: 50000,
                val: -0.111,
            },
            {
                tm: 60000,
                val: -0.106,
            },
            {
                tm: 70000,
                val: -0.090,
            },
            {
                tm: 80000,
                val: -0.065,
            },
            {
                tm: 90000,
                val: -0.034,
            },
            {
                tm: 100000,
                val: 0,
            },
        ],
    },
    {
        delay: 1324,
        duration: 332,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 10000,
                val: -0.011,
            },
            {
                tm: 20000,
                val: -0.022,
            },
            {
                tm: 30000,
                val: -0.030,
            },
            {
                tm: 40000,
                val: -0.035,
            },
            {
                tm: 50000,
                val: -0.037,
            },
            {
                tm: 60000,
                val: -0.035,
            },
            {
                tm: 70000,
                val: -0.030,
            },
            {
                tm: 80000,
                val: -0.022,
            },
            {
                tm: 90000,
                val: -0.011,
            },
            {
                tm: 100000,
                val: 0,
            },
        ],
    },
    {
        delay: 1656,
        duration: 164,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 10000,
                val: -0.004,
            },
            {
                tm: 20000,
                val: -0.007,
            },
            {
                tm: 30000,
                val: -0.010,
            },
            {
                tm: 40000,
                val: -0.012,
            },
            {
                tm: 50000,
                val: -0.0123,
            },
            {
                tm: 60000,
                val: -0.012,
            },
            {
                tm: 70000,
                val: -0.010,
            },
            {
                tm: 80000,
                val: -0.007,
            },
            {
                tm: 90000,
                val: -0.004,
            },
            {
                tm: 100000,
                val: 0,
            },
        ],
    },
    {
        delay: 1820,
        duration: 180,
        tav: [
            {
                tm: 0,
                val: 0,
            },
            {
                tm: 100000,
                val: '+ppt_h',
            },
        ],
    },
];
const scaleList = [
    {
        duration: 26,
        value: 0.6,
    },
    {
        duration: 26,
        value: 0.8,
    },
    {
        duration: 26,
        value: 0.9,
    },
    {
        duration: 26,
        value: 0.95,
    },
    {
        duration: 166,
        value: 1,
    },
];

/**
 * 将 tmFilter 处理成 [`时间进度`， `变量值`]数组
 * NOTE: tmFilter 取自 oxml 中 p:cTn 节点 tmFilter 属性
 * `变量值` 对应 p:cTn 节点相同 p:anim 父节点下的 p:tav 节点 fmla 属性中 `$` 值
 * @example '0,0; 0.14,0.31; 0.43,0.73; 0.71,0.91; 1.0,1.0' ➡️ [[0,0],  [0.14,0.31], [0.43,0.73], [0.71,0.91], [1.0,1.0]]
 * @param tmFilter
 * @returns
 */
const tmFilterParser = (tmFilter: string): number[][] => {
    if (!tmFilter) return [];
    const filterNode = tmFilter.split(';');
    return filterNode.map((node) => {
        if (!node) return [];
        const filter = node.split(',');
        return [Number(filter[0]), Number(filter[1])];
    });
};


/**
 * 获取弹跳动画路径终点变换
 * @param timeNode 动画节点信息
 * @param playMode 播放模式，编辑预览或播放
 * @param animClass 入场还或退场，默认视为入场
 * @returns AnimationStyle
 */
export const getBounceToStyle = (timeNode: IAnimModel, playMode: PLAY_MODE, animClass = presetClassTypes.ENTR) => {
    if (!isRightAnimType(timeNode, playMode, [AnimType.EntrBounce, AnimType.ExitBounce])) return;
    const { slideWidth, slideHeight } = getSlideWidthAndHeight(timeNode, playMode);
    const dur = getAnimDuration(timeNode, playMode);
    const objHeight = timeNode.elementModel.props.style.height;

    const tmFilterStrX = '0,0; 0.14,0.31; 0.43,0.73; 0.71,0.91; 1.0,1.0';
    // NOTE: 位移取自 tmFilter，时间节点选用 yTavList 中时间节点
    const tmFilterX = tmFilterParser(tmFilterStrX);
    const translateY: TAnimBrevity[] = [];
    const translateX: TAnimBrevity[] = [];
    const scaleY: TAnimBrevity[] = [];
    const offsetX = 0.25 * slideWidth;
    // exitYTavList  中后一组 tav 数组的移动位置相对于前一组的最后位置，所以此处留一个最后移动的相对位移
    let lastMoveY = 0;
    let lastMoveX = animClass === presetClassTypes.ENTR ? - slideWidth * 0.25 : 0;
    const tavList = animClass === presetClassTypes.ENTR ? entrYTavList : exitYTavList;

    tavList.forEach((tavGrp, idx) => {
        const tavGrpDuration = (tavGrp.duration / DEFAULT_ANIM_DURATION) * dur;

        const list = tavGrp.tav;
        const translateXList: TAnimBrevity[] = [];
        const translateYList: TAnimBrevity[] = [];

        const count = list.length;
        let offset = 0;
        if (idx === tavList.length - 1) {
            // 最后一项过渡完最后剩余距离
            offset = animClass === presetClassTypes.ENTR ? -lastMoveX : 0;
        } else {
            offset = offsetX * (tmFilterX[idx + 1][1] - tmFilterX[idx][1]) / count;
        }

        const scaleRecoveryDuration = ((tavGrp.duration - scaleList[idx].duration) / DEFAULT_ANIM_DURATION * dur) / (count - 1);

        list.forEach((tav, i) => {
            const duration = i === 0 ? 0 :  (tav.tm - list[i - 1].tm) / 100000 * tavGrpDuration;
            const valueY = _isNumber(tav.val) ? (tav.val * slideHeight) + lastMoveY : objHeight + lastMoveY;
            const valueX = lastMoveX + (i * offset);
            translateYList.push({ duration, value: valueY });
            translateXList.push({ duration, value: valueX });
            if (i === 0 && idx !== 0) {
                const durScale = scaleList[idx].duration / DEFAULT_ANIM_DURATION * dur;
                const { value = 1 } = scaleList[idx - 1];
                scaleY.push({ duration: durScale, value });
            } else {
                scaleY.push({ duration: scaleRecoveryDuration, value: 1 });
            }
        });

        lastMoveY = translateYList[translateYList.length - 1].value;
        lastMoveX = translateXList[translateXList.length - 1].value;

        translateY.push(...translateYList);
        translateX.push(...translateXList);
    });
    return {
        translateX, translateY, scaleY,
    };
};

